import * as RadixPopover from "@radix-ui/react-popover";
import { Command } from "cmdk";
import { Check, ChevronDown, Plus, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Icon } from "../icons/Icon";
import { cx, DISABLED, FOCUS_RING, TOUCH_TARGET } from "../styles";
import { Skeleton } from "./Skeleton";

/**
 * A search field whose options come from the server.
 *
 * Every screen in this product that picks a product, a store, a driver or a
 * city needs one, and every one of them has the same two problems.
 *
 * **The debounce.** A request per keystroke on "corrugated mailer" is
 * seventeen requests, sixteen of which are already stale when they arrive.
 * 250ms is long enough to swallow a burst of typing and short enough that a
 * deliberate pause feels answered.
 *
 * **The cancellation.** This is the one that produces a real bug rather than
 * just load. Type `ab`, then `abc`; if the first request is slower than the
 * second, its results land last and the list shows matches for a query nobody
 * is looking at. Every request gets an `AbortSignal` and the previous one is
 * aborted before the next goes out, so the list can only ever show the current
 * query's answer.
 *
 * `cmdk` supplies the listbox keyboard behaviour — arrows, Enter, Home and End,
 * and the `aria-activedescendant` wiring that tells a screen reader which
 * option is highlighted without moving focus out of the input.
 */

export interface ComboboxOption {
	value: string;
	label: string;
	/** A second line: a SKU under a name, a city under a store. */
	hint?: string;
	disabled?: boolean;
}

/** How long to wait after the last keystroke before asking the server. */
export const COMBOBOX_DEBOUNCE_MS = 250;

export interface ComboboxProps {
	/**
	 * Fetches the matches for a query.
	 *
	 * The signal is aborted when a newer query supersedes this one, and when the
	 * component unmounts. An implementation that ignores it still works and
	 * still races; passing it to `fetch` is what makes the race impossible.
	 */
	loadOptions: (query: string, signal: AbortSignal) => Promise<ComboboxOption[]>;
	value?: string | null;
	onValueChange?: (value: string | null) => void;
	/** The chosen option's label, when the caller already knows it. */
	selectedLabel?: string;
	placeholder?: string;
	/** Offers to create what was typed, when nothing matched. */
	onCreate?: (query: string) => void;
	createLabel?: (query: string) => string;
	emptyLabel?: string;
	errorLabel?: string;
	loadingLabel?: string;
	clearLabel?: string;
	disabled?: boolean;
	id?: string;
	"aria-label"?: string | undefined;
	"aria-describedby"?: string | undefined;
	"aria-invalid"?: true | undefined;
	className?: string;
}

type Status = "idle" | "loading" | "ready" | "failed";

export function Combobox({
	loadOptions,
	value = null,
	onValueChange,
	selectedLabel,
	placeholder,
	onCreate,
	createLabel,
	emptyLabel = "No matches",
	errorLabel = "Could not load options",
	loadingLabel = "Searching",
	clearLabel = "Clear",
	disabled = false,
	id,
	className,
	"aria-label": ariaLabel,
	"aria-describedby": describedBy,
	"aria-invalid": invalid,
}: ComboboxProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [options, setOptions] = useState<ComboboxOption[]>([]);
	const [status, setStatus] = useState<Status>("idle");

	// The in-flight request, so the next query can cancel it. A ref rather than
	// state: aborting must not wait for a render.
	const inFlight = useRef<AbortController | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	// The latest loader, in a ref (ADJ-M03-1). The search below used to depend
	// on its identity, so a caller passing an inline function re-ran it on
	// every render — one request every 250ms for as long as the list was open.
	const loader = useRef(loadOptions);
	loader.current = loadOptions;

	const search = useCallback(async (text: string) => {
		inFlight.current?.abort();
		const controller = new AbortController();
		inFlight.current = controller;

		setStatus("loading");
		try {
			const found = await loader.current(text, controller.signal);
			if (controller.signal.aborted) {
				return;
			}
			setOptions(found);
			setStatus("ready");
		} catch {
			// An abort lands here too, and must not be reported: the request was
			// cancelled on purpose, and showing "could not load" for it would
			// flash an error on every keystroke.
			if (!controller.signal.aborted) {
				setStatus("failed");
			}
		}
	}, []);

	// Debounced, and re-run when the list opens so the first press shows
	// something rather than an empty box.
	useEffect(() => {
		if (!open) {
			return;
		}
		const timer = window.setTimeout(() => void search(query), COMBOBOX_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [open, query, search]);

	// Nothing in flight should outlive the component.
	useEffect(() => () => inFlight.current?.abort(), []);

	const chosen = options.find((option) => option.value === value);
	const label = chosen?.label ?? selectedLabel ?? "";
	const canCreate =
		onCreate !== undefined && query.trim() !== "" && status === "ready" && options.length === 0;

	return (
		<RadixPopover.Root open={open} onOpenChange={setOpen}>
			<div
				className={cx(
					"flex h-9 items-center gap-1 rounded-md border border-border-strong bg-bg-surface",
					"focus-within:ring-3 focus-within:ring-ring-halo focus-within:border-ring",
					"aria-[invalid]:border-danger-border aria-[invalid]:ring-3 aria-[invalid]:ring-danger-bg",
					TOUCH_TARGET,
					DISABLED,
					className,
				)}
				aria-invalid={invalid}
			>
				<RadixPopover.Trigger asChild>
					<button
						type="button"
						id={id}
						disabled={disabled}
						aria-label={ariaLabel}
						aria-describedby={describedBy}
						className={cx(
							"flex min-w-0 flex-1 items-center px-2.5 text-start text-[13px] cursor-pointer",
							label === "" ? "text-fg-subtle" : "text-fg",
							"h-full outline-none",
						)}
					>
						<span className="truncate">{label === "" ? placeholder : label}</span>
					</button>
				</RadixPopover.Trigger>

				{value !== null && onValueChange !== undefined && (
					<button
						type="button"
						aria-label={clearLabel}
						onClick={() => onValueChange(null)}
						className={cx("shrink-0 px-1 text-fg-subtle hover:text-fg cursor-pointer", FOCUS_RING)}
					>
						<Icon as={X} size={14} />
					</button>
				)}
				<Icon as={ChevronDown} size={16} className="me-2 shrink-0 text-fg-subtle" />
			</div>

			<RadixPopover.Portal>
				<RadixPopover.Content
					align="start"
					sideOffset={4}
					className={cx(
						"z-[var(--z-popover)] w-[var(--radix-popover-trigger-width)] overflow-hidden",
						"rounded-md border border-border bg-bg-raised shadow-overlay",
					)}
					// Focus goes to the search field, not to the list: the person is
					// typing, and `aria-activedescendant` is what tells a screen reader
					// which option the arrows are on.
					onOpenAutoFocus={(event) => {
						event.preventDefault();
						inputRef.current?.focus();
					}}
				>
					<Command shouldFilter={false} label={ariaLabel ?? placeholder ?? ""}>
						<div className="border-b border-border p-2">
							<Command.Input
								ref={inputRef}
								value={query}
								onValueChange={setQuery}
								placeholder={placeholder}
								className="h-8 w-full bg-transparent px-1 text-[13px] text-fg outline-none placeholder:text-fg-subtle"
							/>
						</div>

						<Command.List className="max-h-64 overflow-y-auto p-1" data-scroll="1">
							{status === "loading" && (
								// Rows rather than a spinner, so the list does not collapse to
								// nothing and jump back when the answer lands.
								<div
									// role="status" rather than a bare div: a label has to sit on
									// an element whose role supports naming, and a list that is
									// still loading is exactly a status.
									role="status"
									aria-label={loadingLabel}
									className="flex flex-col gap-1 p-1"
								>
									<Skeleton className="h-6 w-full" />
									<Skeleton className="h-6 w-4/5" />
									<Skeleton className="h-6 w-3/5" />
								</div>
							)}

							{status === "failed" && (
								<div role="alert" className="p-3 text-[12px] text-danger-fg">
									{errorLabel}
								</div>
							)}

							{status === "ready" && options.length === 0 && !canCreate && (
								<Command.Empty className="p-3 text-[12px] text-fg-subtle">
									{emptyLabel}
								</Command.Empty>
							)}

							{status === "ready" &&
								options.map((option) => (
									<Command.Item
										key={option.value}
										value={option.value}
										{...(option.disabled === undefined ? {} : { disabled: option.disabled })}
										onSelect={() => {
											onValueChange?.(option.value);
											setOpen(false);
										}}
										className={cx(
											"flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-[13px]",
											"data-[selected=true]:bg-brand-subtle data-[selected=true]:text-brand-ink",
											"data-[disabled=true]:cursor-not-allowed data-[disabled=true]:text-fg-subtle",
											"max-sm:min-h-12",
										)}
									>
										<span className="w-3.5 shrink-0">
											{option.value === value && <Icon as={Check} size={14} />}
										</span>
										<span className="min-w-0 flex-1">
											<span className="block truncate">{option.label}</span>
											{option.hint !== undefined && (
												<span className="block truncate text-[11px] text-fg-subtle">
													{option.hint}
												</span>
											)}
										</span>
									</Command.Item>
								))}

							{canCreate && (
								<Command.Item
									value={`__create__${query}`}
									onSelect={() => {
										onCreate?.(query);
										setOpen(false);
									}}
									className={cx(
										"flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-[13px] text-brand-ink",
										"data-[selected=true]:bg-brand-subtle max-sm:min-h-12",
									)}
								>
									<Icon as={Plus} size={14} />
									{createLabel?.(query) ?? query}
								</Command.Item>
							)}
						</Command.List>
					</Command>
				</RadixPopover.Content>
			</RadixPopover.Portal>
		</RadixPopover.Root>
	);
}
